/*
  Attar de Astana · motion

  One orchestrated behaviour rather than scattered effects.

  1. Reveal. Anything marked [data-reveal] or [data-reveal-mask] rises and un-blurs once
     as it enters the frame, then is released. Elements already above the fold on load
     are revealed immediately so the first paint is never empty.
  2. Lines. The two line treatments get a cut rather than a fade: the frame opens upward,
     the creative settles down into it, and the line name climbs out letter by letter.
     This file only splits the word and marks the block; the stylesheet owns the timing.
  3. Fall. A banner whose subjects have been cut onto their own layers drops them into
     place as the frame is scrolled up, one after another, and hands the last of each
     drop to a spring.
  4. Tilt. A fine pointer over a line nudges its creative toward the cursor by a few
     pixels and slides a soft light across it, so the block answers your hand without
     ever moving the image far enough to expose an edge. Touch gets neither; a phone's
     only motion on a line is the entrance and the scroll drift.
  5. Parallax. Marked media drifts against the scroll inside a bounded range, driven from
     a single rAF loop so the count of parallax sections does not change the cost.

  Everything degrades to a complete, static page: the CSS only hides content under
  html.js, and reduced-motion users skip both systems entirely.
*/

(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  /* ---------------------------------------------------------------- reveal */

  const revealTargets = () => document.querySelectorAll('[data-reveal], [data-reveal-mask]');

  const revealAll = () => {
    revealTargets().forEach((el) => el.classList.add('is-revealed'));
  };

  const initReveal = () => {
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      revealAll();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      {
        /*
          Fire a little before the element is fully on screen. The move should be
          finishing as it arrives, not starting.
        */
        rootMargin: '0px 0px -12% 0px',
        threshold: 0.01,
      }
    );

    revealTargets().forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.9) {
        // Already in view on load. Reveal without waiting for a scroll that may never come.
        el.classList.add('is-revealed');
        return;
      }
      observer.observe(el);
    });
  };

  /* -------------------------------------------------------------- parallax */

  let parallaxItems = [];
  let ticking = false;

  const readParallax = () => {
    const viewport = window.innerHeight;

    parallaxItems.forEach((item) => {
      const rect = item.el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > viewport) return;

      /*
        -1 when the section sits below the fold, 1 when it has passed above it.
        Multiplying by the section's own range keeps the image overscanned at both
        extremes, so an edge can never appear.
      */
      const centre = rect.top + rect.height / 2;
      const progress = (centre - viewport / 2) / (viewport / 2 + rect.height / 2);
      const clamped = Math.max(-1, Math.min(1, progress));
      item.el.style.setProperty('--parallax-shift', `${(clamped * item.range).toFixed(2)}px`);
    });

    readDrift();
    readFall();
    ticking = false;
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(readParallax);
  };

  const initParallax = () => {
    if (reducedMotion.matches) return;

    parallaxItems = Array.from(document.querySelectorAll('[data-parallax]')).map((el) => ({
      el,
      range: Number.parseFloat(el.dataset.parallax) || 40,
    }));

    if (!parallaxItems.length && !driftBlocks.length && !fallItems.length) return;

    readParallax();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  };

  /* ------------------------------------------------------------------ fall */

  /*
    A banner whose subject has been cut out onto its own layer, so the subject can be
    moved without the scene moving with it.

    The descent is scrubbed: the scroll position is the clock, so the drop answers the
    hand on the wheel and runs backwards if the page is scrolled back up. The landing is
    not scrubbed. Past the touchdown point a class hands the remaining travel to a CSS
    spring, which takes the same three quarters of a second either way. Scrubbing that
    last part would let a slow scroll stretch an impact into a drifting wobble, and an
    impact that can be slowed down does not read as weight.

    The two thresholds are apart on purpose. Landing and un-landing at the same point
    would let a pointer resting exactly on it flicker between the two states.

    Several subjects in one frame each get their own offset, so a row lands left to
    right rather than in one piece. The offsets are subtracted from a shared scroll
    range and every subject keeps the same span of it, so the last one to start still
    falls at the speed of the first, and the sequence reads as four separate arrivals
    rather than one thing sliding.
  */

  let fallItems = [];

  const FALL_LAND = 0.86;
  const FALL_RELEASE = 0.8;

  const readFall = () => {
    const viewport = window.innerHeight;

    fallItems.forEach((item) => {
      // Wide margins: the first reading of a frame has to happen while it is still off
      // screen, or the subject is caught at rest and jumps into the air in full view.
      const rect = item.el.getBoundingClientRect();
      if (rect.bottom < -400 || rect.top > viewport + 400) return;

      // 0 as the frame's top edge clears the bottom of the viewport, 1 once the frame
      // is sitting where a reader would be looking at it. The end point leans on the
      // frame's own height rather than the viewport alone, because a banner taller
      // than the screen cannot centre its subject while its top edge is still on it:
      // timing that off the top edge alone lands the subject down by the bottom
      // margin, which is where the four bottles were arriving before this.
      const from = viewport;
      const to = viewport * 0.3 - rect.height * 0.35;
      const raw = clamp((from - rect.top) / Math.max(from - to, 1), 0, 1);
      const progress = clamp((raw - item.delay) / item.span, 0, 1);

      const landed = item.el.classList.contains('is-landed');
      if (!landed && progress >= FALL_LAND) item.el.classList.add('is-landed');
      else if (landed && progress < FALL_RELEASE) item.el.classList.remove('is-landed');

      const style = item.el.style;

      if (item.el.classList.contains('is-landed')) {
        style.setProperty('--fall-y', '0');
        style.setProperty('--fall-x', '0');
        style.setProperty('--fall-rot', '0');
        style.setProperty('--fall-scale', '1');
        return;
      }

      // Squared, so the subject is let go slowly and arrives quickly. A linear drop
      // reads as a panel sliding rather than as something with weight falling.
      const g = progress * progress;
      // Tilt, drift and approach all scale with the drop. A bottle standing on a table
      // that only falls a ninth of the frame should not swing through the same arc as
      // one falling a quarter of it; at that distance the same angle reads as toppling.
      const arc = item.rise / 25;
      style.setProperty('--fall-y', ((g - 1) * item.rise).toFixed(3));
      style.setProperty('--fall-x', ((1 - g) * 2.4 * arc).toFixed(3));
      style.setProperty('--fall-rot', ((1 - g) * -7 * arc).toFixed(3));
      style.setProperty('--fall-scale', (1 + (1 - g) * 0.05 * arc).toFixed(4));
    });
  };

  const initFall = () => {
    /*
      Under a reduced-motion preference nothing is collected, so nothing is ever
      written, so every variable keeps its resting default and the banner is the
      photograph it was cut from. Same for the stylesheet's own stand-down.
    */
    if (reducedMotion.matches) return;

    fallItems = [];
    document.querySelectorAll('.banner').forEach((banner) => {
      const layers = Array.from(banner.querySelectorAll('.banner__fg[data-fall]'));
      if (!layers.length) return;
      // Read the offsets as fractions of this frame's own scroll range, then give every
      // subject in the frame the same span of what is left. Dividing each by its own
      // remainder instead would make the later ones fall faster to catch up.
      const delays = layers.map((el) => (Number.parseFloat(el.dataset.delay) || 0) / 100);
      const span = Math.max(1 - Math.max(...delays), 0.25);
      layers.forEach((el, i) => {
        fallItems.push({
          el,
          rise: Number.parseFloat(el.dataset.fall) || 9,
          delay: delays[i],
          span,
        });
      });
    });
  };

  /* ----------------------------------------------------------------- lines */

  /*
    Two jobs, both in service of one move: the line name arrives as type, not as a
    block of text that faded in.

    Splitting. A display word is cut into per-letter cells so the stylesheet can stagger
    them out of a clipped slot and run the same stagger again under the pointer. The word
    is kept whole for a screen reader through aria-label, and the cells are hidden from
    it. Words are wrapped so a name still breaks between words and never mid-word.

    Drift. Each block reports where it sits in the frame as --drift, -1 below to 1 above,
    read inside the parallax loop so the scroll is measured once for the whole page. The
    plate moves as a unit on it. Nothing inside the frame moves against the frame, so the
    scroll can never crop a creative.
  */

  let driftBlocks = [];

  const splitDisplay = (el) => {
    const text = (el.textContent || '').trim();
    if (!text || el.dataset.split === 'on') return;

    el.dataset.split = 'on';
    el.setAttribute('aria-label', text);

    const frag = document.createDocumentFragment();
    let index = 0;

    text.split(/\s+/).forEach((word, wordIndex) => {
      if (wordIndex > 0) frag.appendChild(document.createTextNode(' '));

      const wordEl = document.createElement('span');
      wordEl.className = 'ln-w';
      wordEl.setAttribute('aria-hidden', 'true');

      // Array.from, not split(''), so a surrogate pair stays one letter.
      Array.from(word).forEach((character) => {
        const cell = document.createElement('span');
        cell.className = 'ln-c';
        const glyph = document.createElement('span');
        glyph.textContent = character;
        glyph.style.setProperty('--ci', index);
        index += 1;
        cell.appendChild(glyph);
        wordEl.appendChild(cell);
      });

      frag.appendChild(wordEl);
    });

    el.replaceChildren(frag);
  };

  const readDrift = () => {
    const viewport = window.innerHeight;
    driftBlocks.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > viewport) return;
      const centre = rect.top + rect.height / 2;
      const progress = (centre - viewport / 2) / (viewport / 2 + rect.height / 2);
      const clamped = Math.max(-1, Math.min(1, progress));
      el.style.setProperty('--drift', clamped.toFixed(3));
    });
  };

  const initLines = (root = document) => {
    /*
      Under a reduced-motion preference the word is left as one run of text. Every
      staggered rule keys off the cells, so not making them stands the whole system
      down without a single override.
    */
    if (reducedMotion.matches) return;

    root.querySelectorAll('.col-billboard, .col-room').forEach((block) => {
      // The theme editor reloads a section without discarding the document.
      if (block.dataset.lines === 'on') return;
      block.dataset.lines = 'on';
      driftBlocks.push(block);

      block
        .querySelectorAll('.col-billboard__name, .col-room__name')
        .forEach(splitDisplay);
    });
  };

  /* ------------------------------------------------------------------ tilt */

  /*
    The interactive half of a line, separate from its entrance. A fine pointer moving
    over the block nudges the creative a few pixels toward it (--tx/--ty, read by the
    media anchor's own transform) and slides a soft highlight across the image
    (--px/--py, read by a ::after the stylesheet only shows on hover). Both are small
    and eased, on purpose: the last version of this moved a 14rem word two full rem
    against the raw pointer position, which tracked the cursor closely enough to read
    as jitter rather than depth. A few pixels of image, eased over half a second, reads
    as a considered material responding to you instead.
  */

  let tiltBlocks = [];

  const initTilt = (root = document) => {
    if (reducedMotion.matches) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    root.querySelectorAll('.col-billboard, .col-room').forEach((block) => {
      // The theme editor reloads a section without discarding the document.
      if (block.dataset.tilt === 'on') return;
      const media = block.querySelector('.col-billboard__media, .col-room__media');
      if (!media) return;

      block.dataset.tilt = 'on';
      tiltBlocks.push(block);

      let frame = null;
      let queued = null;

      const flush = () => {
        frame = null;
        if (!queued) return;
        const [tx, ty, px, py] = queued;
        block.style.setProperty('--tx', `${tx.toFixed(2)}px`);
        block.style.setProperty('--ty', `${ty.toFixed(2)}px`);
        media.style.setProperty('--px', `${px.toFixed(1)}%`);
        media.style.setProperty('--py', `${py.toFixed(1)}%`);
        queued = null;
      };

      block.addEventListener(
        'pointermove',
        (event) => {
          if (event.pointerType === 'touch') return;

          const blockBox = block.getBoundingClientRect();
          const mediaBox = media.getBoundingClientRect();
          if (!blockBox.width || !mediaBox.width) return;

          // The tilt reads off the whole card, so it still answers a hand resting over
          // the copy, not only over the image itself.
          const bx = ((event.clientX - blockBox.left) / blockBox.width - 0.5) * 2;
          const by = ((event.clientY - blockBox.top) / blockBox.height - 0.5) * 2;

          // The spotlight reads off the image alone, and is allowed past its edges so it
          // parks at the nearest one rather than snapping away the moment the pointer
          // leaves the frame for the copy below it.
          const px = ((event.clientX - mediaBox.left) / mediaBox.width) * 100;
          const py = ((event.clientY - mediaBox.top) / mediaBox.height) * 100;

          queued = [clamp(bx, -1, 1) * 7, clamp(by, -1, 1) * 5, clamp(px, -20, 120), clamp(py, -20, 120)];
          if (!frame) frame = window.requestAnimationFrame(flush);
        },
        { passive: true }
      );

      block.addEventListener('pointerleave', () => {
        block.style.setProperty('--tx', '0px');
        block.style.setProperty('--ty', '0px');
      });
    });
  };

  /* ------------------------------------------------------------------ rake */

  /*
    The hero's key light follows the pointer rather than sitting dead centre, and a
    specular spot tracks the same source across the glass.

    Two things justify the code. The bloom is positioned in the section's coordinate
    space and the spot in the bottle's, so the pointer has to be converted twice. And
    a visitor who never moves a pointer would otherwise meet a frame that looks broken
    rather than still, so the light drifts on its own on arrival and yields the moment
    a real pointer shows up.
  */

  const RAKE_DRIFT_MS = 6000;

  const initRake = (root = document) => {
    if (reducedMotion.matches || !('IntersectionObserver' in window)) return;

    root.querySelectorAll('.hero-cine').forEach((hero) => {
      // The theme editor reloads a section without discarding the document.
      if (hero.dataset.rake === 'on') return;
      const glass = hero.querySelector('.hero-cine__bottle-float');
      if (!glass) return;

      hero.dataset.rake = 'on';
      hero.classList.add('is-rake');

      let taken = false;
      let frame = null;
      let queued = null;

      /* Both positions are a percentage of the section box. */
      const place = (xPercent, yPercent) => {
        // Read both boxes before writing, or the first write invalidates the second read.
        const frameBox = hero.getBoundingClientRect();
        const glassBox = glass.getBoundingClientRect();
        if (!frameBox.width || !glassBox.width) return;

        const pointX = frameBox.left + (xPercent / 100) * frameBox.width;
        const pointY = frameBox.top + (yPercent / 100) * frameBox.height;
        const sheenX = ((pointX - glassBox.left) / glassBox.width) * 100;
        const sheenY = ((pointY - glassBox.top) / glassBox.height) * 100;

        hero.style.setProperty('--rake-x', `${xPercent.toFixed(1)}%`);
        hero.style.setProperty('--rake-y', `${yPercent.toFixed(1)}%`);
        /*
          Allowed past both edges so the spot can travel off the glass and back rather
          than sticking to the nearest edge, but bounded so a pointer at the far side of
          a wide frame cannot push the gradient somewhere it costs a repaint for nothing.
        */
        hero.style.setProperty('--rake-sheen-x', `${clamp(sheenX, -20, 120).toFixed(1)}%`);
        hero.style.setProperty('--rake-sheen-y', `${clamp(sheenY, -20, 120).toFixed(1)}%`);
        hero.classList.add('is-lit');
      };

      const flush = () => {
        frame = null;
        if (!queued) return;
        place(queued[0], queued[1]);
        queued = null;
      };

      hero.addEventListener(
        'pointermove',
        (event) => {
          /*
            A touch drag on a phone is a scroll, not an attempt to hold the light. Taking
            it over would both yank the highlight and kill the drift that is the only
            movement a phone ever gets here.
          */
          if (event.pointerType === 'touch') return;

          taken = true;
          const box = hero.getBoundingClientRect();
          if (!box.width) return;

          // One placement per frame however fast the pointer reports.
          queued = [
            ((event.clientX - box.left) / box.width) * 100,
            ((event.clientY - box.top) / box.height) * 100,
          ];
          if (!frame) frame = window.requestAnimationFrame(flush);
        },
        { passive: true }
      );

      const drift = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            drift.unobserve(entry.target);

            let start = null;
            const step = (now) => {
              if (taken || reducedMotion.matches) return;
              if (start === null) start = now;
              const elapsed = now - start;
              if (elapsed > RAKE_DRIFT_MS) return;
              /*
                Two periods that do not divide into one another, so the path does not
                close into a visible loop before it stops.
              */
              place(50 + 22 * Math.sin(elapsed / 1600), 44 + 9 * Math.cos(elapsed / 2100));
              window.requestAnimationFrame(step);
            };

            window.requestAnimationFrame(step);
          });
        },
        { threshold: 0.3 }
      );

      drift.observe(hero);
    });
  };

  /* ------------------------------------------------------------------ boot */

  const start = () => {
    // Split before reveal: a block above the fold is revealed on sight, and a word
    // cut up after that moment would skip its own entrance.
    initLines();
    initReveal();
    initTilt();
    // Collected before the loop starts, so the loop's own first pass places them.
    initFall();
    initParallax();
    initRake();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  // Sections added or reordered in the theme editor need the same treatment.
  document.addEventListener('shopify:section:load', (event) => {
    initLines(event.target);
    revealAll();
    initTilt(event.target);
    initFall();
    initParallax();
    initRake(event.target);
  });

  // Honour a mid-session change to the motion preference rather than only reading it once.
  const onPreferenceChange = () => {
    if (!reducedMotion.matches) return;
    revealAll();
    // The stylesheet stands the light down on its own; this stops the drift as well.
    document.querySelectorAll('.hero-cine.is-rake').forEach((hero) => {
      hero.classList.remove('is-rake', 'is-lit');
    });
    // And the lines park at rest.
    driftBlocks.forEach((block) => {
      block.style.setProperty('--drift', '0');
    });
    tiltBlocks.forEach((block) => {
      block.style.setProperty('--tx', '0px');
      block.style.setProperty('--ty', '0px');
    });
    // And any cut-out subject is put back where it was cut from.
    fallItems.forEach((item) => {
      item.el.classList.remove('is-landed');
      item.el.removeAttribute('style');
    });
    fallItems = [];
  };

  if (typeof reducedMotion.addEventListener === 'function') {
    reducedMotion.addEventListener('change', onPreferenceChange);
  }
})();
